import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  TrendingUp,
  Wallet,
  AlertCircle,
  Settings,
  ChevronDown,
  FileText,
  Eye,
  Plus,
  MapPin,
  Building2,
} from "lucide-react";
import BlockAccountModal from "../BlockAccountModal";
import {
  errorToast,
  successToast,
} from "../../../../../services/toast.service";
import {
  useLazyFetchVendorStoreQuery,
  useUpdateCustomerStatusMutation,
} from "../../../../../services/baseQuery";
import ControlledAccordions from "../../../VoucherModule/Components/Collapsable";

const CustomerOverviewTab = ({ customerData, vcConfigSettings, refetch }) => {
  {
    const [configVisibility, setConfigVisibility] = useState(false);
    const [vouchervisibility, setVoucherVisibility] = useState(false);
    const [
      triggerFetchVendorStore,
      { isFetching: fetchingVendorStore, data: vendorStoreData },
    ] = useLazyFetchVendorStoreQuery();
    const [orderValidation, setOrderValidation] = useState({
      validationType: "USER_SPECIFIC",
      minOrderQty: 0,
      minOrderValue: 0,
      validationCondition: "ANY_ONE",
    });
    const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
    const [updateStatus, { isLoading: isUpdatingStatus }] =
      useUpdateCustomerStatusMutation();

    // Use customerData instead of hardcoded values
    const {
      businessName,
      storeId,
      ownerName,
      registeredPhoneNo,
      storeType,
      platformApproved,
      shopAddress,
      deliveryAddress,
      // ... other fields
    } = customerData;

    useEffect(() => {
      if (
        vcConfigSettings.settings.orderValidation.validationType ===
        "COMPANY_SETTINGS"
      ) {
        triggerFetchVendorStore();
      } else setOrderValidation(vcConfigSettings.settings.orderValidation);
    }, [vcConfigSettings]);

    useEffect(() => {
      if (vendorStoreData) {
        const { minOrderQty, minOrderValue, validationCondition } =
          vendorStoreData.data.store.orderValidation;
        setOrderValidation({
          validationType: "COMPANY_SETTINGS",
          minOrderQty,
          minOrderValue,
          validationCondition,
        });
      }
    }, [vendorStoreData]);

    // Sample data - replace with actual data
    const metrics = [
      {
        label: "Total Sales",
        value: "₹45,00,000",
        change: "+12.5%",
        trend: "up",
        icon: TrendingUp,
        color: "emerald",
      },
      {
        label: "Outstanding Balance",
        value: "₹5,00,000",
        change: "-2.3%",
        trend: "down",
        icon: Wallet,
        color: "rose",
      },
      {
        label: "Credit Limit",
        value: `₹${vcConfigSettings.settings.creditLimit.toLocaleString()}`,
        used: "50%",
        icon: AlertCircle,
        color: "blue",
      },
    ];

    // Sample brand visibility data - replace with actual data
    const brandVisibility = {
      visibilityType: vcConfigSettings.settings.brandVisibility.visibilityType, // or "ALL_COMPANY_BRANDS"
      removedBrands: vcConfigSettings.settings.brandVisibility?.removedBrands,
    };

    console.log(brandVisibility.removedBrands, "removedBrands");

    const renderBrandVisibility = () => {
      if (brandVisibility.visibilityType === "ALL_COMPANY_BRANDS") {
        return (
          <div className="bg-green-50 rounded-lg p-3 flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <p className="text-sm text-green-700">All Company Brands Visible</p>
          </div>
        );
      }

      return (
        <div className="space-y-3">
          <div className="bg-amber-50 rounded-lg p-3 flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <p className="text-sm text-amber-700">Selected Brands Hidden</p>
          </div>
          <div className="mt-2">
            <p className="text-xs font-medium text-gray-500 mb-2">
              Hidden Brands:
            </p>
            <div className="flex flex-wrap gap-2">
              {brandVisibility.removedBrands.map((brand, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {brand.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      );
    };

    const handleStatusUpdate = async () => {
      try {
        await updateStatus({
          customerId: customerData._id,
          status: !vcConfigSettings.customerBlocked,
        }).unwrap();

        successToast(
          `Customer ${
            vcConfigSettings.customerBlocked ? "unblocked" : "blocked"
          } successfully`,
        );
        setIsBlockModalOpen(false);

        // Refetch customer details after successful update
        if (refetch) {
          await refetch();
        }
      } catch (error) {
        errorToast(
          error.data?.error?.message || "Failed to update customer status",
        );
      }
    };

    return (
      <div className="space-y-6 max-w-[1400px] mx-auto">
        {/* Header Section */}
        <div
          className={`bg-white rounded-xl shadow-sm overflow-hidden
            ${
              vcConfigSettings.customerBlocked
                ? "border-2 border-red-200 relative"
                : "border border-gray-200"
            }`}>
          {vcConfigSettings.customerBlocked && (
            <div className="bg-red-50 px-6 py-2 border-b border-red-200">
              <div className="flex items-center space-x-2 text-red-700">
                <AlertCircle className="w-4 h-4" />
                <p className="text-sm font-medium">
                  This customer account is currently blocked
                </p>
              </div>
            </div>
          )}

          <div className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <div className="flex items-center space-x-3">
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {businessName}
                  </h2>
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-50 text-[#5652b0]">
                    RETAILER
                  </span>
                  {platformApproved ? (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-50 text-green-700">
                      Verified
                    </span>
                  ) : (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-50 text-red-700">
                      Unverified
                    </span>
                  )}
                  {vcConfigSettings.customerBlocked && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-50 text-red-700">
                      Blocked
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>ID: {storeId}</span>
                  <span>•</span>
                  <span>
                    Joined:{" "}
                    {new Date("2024-05-13").toLocaleDateString("en-US", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  Edit Details
                </button> */}
                <button
                  onClick={() => setIsBlockModalOpen(true)}
                  className={`inline-flex items-center px-4 py-2 border rounded-lg text-sm font-medium ${
                    !vcConfigSettings.customerBlocked
                      ? "border-red-300 text-red-700 hover:bg-red-50"
                      : "border-green-300 text-green-700 hover:bg-green-50"
                  }`}>
                  {!vcConfigSettings.customerBlocked
                    ? "Block Account"
                    : "Unblock Account"}
                </button>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x border-t">
            {metrics.map((metric, idx) => (
              <div key={idx} className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {metric.label}
                    </p>
                    <p className="mt-2 flex items-baseline">
                      <span className="text-2xl font-semibold text-gray-900">
                        {metric.value}
                      </span>
                      {metric.change && (
                        <span
                          className={`ml-2 text-sm font-medium text-${
                            metric.trend === "up" ? "emerald" : "rose"
                          }-600`}>
                          {metric.change}
                        </span>
                      )}
                    </p>
                    {metric.used && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: metric.used }}></div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {metric.used} utilized
                        </p>
                      </div>
                    )}
                  </div>
                  <metric.icon
                    className={`w-8 h-8 text-${metric.color}-600 bg-${metric.color}-50 p-1.5 rounded-lg`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Business Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Building2 className="w-5 h-5 text-gray-400" />
                <h3 className="text-lg font-medium text-gray-900">
                  Business Details
                </h3>
              </div>
            </div>
            <div className="p-6 grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Business Name
                </p>
                <p className="mt-1 text-sm text-gray-900">{businessName}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Owner Name</p>
                <p className="mt-1 text-sm text-gray-900">{ownerName}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Phone Number
                </p>
                <p className="mt-1 text-sm text-gray-900">
                  {registeredPhoneNo}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">GSTIN</p>
                <p className="mt-1 text-sm text-gray-900">22AABCQA0000C1Z6</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Dealer Type</p>
                <p className="mt-1 text-sm text-gray-900">Regular</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Store Type</p>
                <p className="mt-1 text-sm text-gray-900">{storeType}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <h3 className="text-lg font-medium text-gray-900">
                  Address Information
                </h3>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  Shop Address
                </p>
                <address className="not-italic text-sm text-gray-900 leading-relaxed">
                  {shopAddress.localAddress}, {shopAddress.locality}
                  <br />
                  {shopAddress.city_district}, {shopAddress.state} -{" "}
                  {shopAddress.pincode}
                </address>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  Delivery Address
                </p>
                <address className="not-italic text-sm text-gray-900 leading-relaxed">
                  {deliveryAddress.localAddress}, {deliveryAddress.locality}
                  <br />
                  {deliveryAddress.city_district}, {deliveryAddress.state} -{" "}
                  {deliveryAddress.pincode}
                </address>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Notes Section - Add this here */}
        {vcConfigSettings.additionalNotes && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center space-x-3">
              <FileText className="w-5 h-5 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900">Admin Notes</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                {vcConfigSettings.additionalNotes}
              </p>
            </div>
          </div>
        )}

        {/* Configuration Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <button
            onClick={() => setConfigVisibility(!configVisibility)}
            className="w-full px-6 py-4 flex items-center justify-between border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Settings className="w-5 h-5 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900">
                Configuration Settings
              </h3>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                configVisibility ? "transform rotate-180" : ""
              }`}
            />
          </button>

          {configVisibility && (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Brand Visibility Section */}
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Brand Visibility
                  </p>
                  <div className="mt-2">{renderBrandVisibility()}</div>
                </div>
              </div>

              {/* Order Validation Section */}
              <div className="space-y-6">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Order Validation
                  </p>
                  <div className="mt-2">
                    {vcConfigSettings.settings.orderValidation
                      .validationType === "USER_SPECIFIC" ? (
                      <div className="bg-purple-50 rounded-lg p-3 flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-[#5652B0]" />
                        <p className="text-sm text-[#5652B0]">
                          User Specific Validation
                        </p>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-gray-500" />
                        <p className="text-sm text-gray-700">
                          Default Company Validation
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-500">
                      Minimum Order
                    </p>
                    <span
                      className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        orderValidation.validationCondition === "BOTH"
                          ? "bg-amber-50 text-amber-700"
                          : "bg-green-50 text-green-700"
                      }`}>
                      {orderValidation.validationCondition === "BOTH"
                        ? "Both Required"
                        : "Any One Required"}
                    </span>
                  </div>
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div className={`bg-gray-50 rounded-lg p-3 `}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">Value</p>
                          {orderValidation.validationCondition === "BOTH" && (
                            <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-900">
                          ₹{orderValidation.minOrderValue.toLocaleString()}
                        </p>
                      </div>
                      <div className={`bg-gray-50 rounded-lg p-3`}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">Quantity</p>
                          {orderValidation.validationCondition === "BOTH" && (
                            <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-900">
                          {orderValidation.minOrderQty.toLocaleString()} pairs
                        </p>
                      </div>
                    </div>

                    {/* Validation Condition Description */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">
                        Validation Rule
                      </p>
                      <p className="text-sm text-gray-700">
                        {orderValidation.validationCondition === "BOTH" ? (
                          <>
                            Order must meet{" "}
                            <span className="font-medium">both</span> minimum
                            value and quantity requirements
                          </>
                        ) : (
                          <>
                            Order must meet{" "}
                            <span className="font-medium">either</span> minimum
                            value or quantity requirement
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Vouchers Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <button
            onClick={() => setVoucherVisibility(!vouchervisibility)}
            className="w-full px-6 py-4 flex items-center justify-between border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900">
                Firm Vouchers
              </h3>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-[#5652B0]">
                3 Active
              </span>

              {/* Info Button with Tooltip */}
              <div className="relative group">
                <button
                  className="p-1.5 rounded-full hover:bg-purple-50 text-gray-400 hover:text-[#5652B0] transition-colors"
                  onClick={e => e.stopPropagation()}>
                  <Eye className="w-4 h-4" />
                </button>

                {/* Tooltip */}
                <div className="absolute left-0 top-full mt-2 w-72 p-3 bg-white rounded-lg shadow-lg border border-gray-200 hidden group-hover:block z-10">
                  <div className="flex items-start space-x-2">
                    <div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        These vouchers are only for{" "}
                        <span className="font-medium">{businessName}</span>.
                      </p>
                      <div className="mt-2 flex items-center">
                        <NavLink
                          to="/voucher/viewvoucher"
                          className="text-sm text-[#5652B0] hover:text-[#5652B0] font-medium flex items-center"
                          onClick={e => e.stopPropagation()}>
                          View all vouchers
                          <ChevronDown className="w-4 h-4 ml-1 rotate-[-90deg]" />
                        </NavLink>
                      </div>
                    </div>
                  </div>
                  {/* Tooltip Arrow */}
                  <div className="absolute -top-2 left-4 w-4 h-4 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
                </div>
              </div>
            </div>

            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                vouchervisibility ? "transform rotate-180" : ""
              }`}
            />
          </button>

          {vouchervisibility && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <button className="px-3 py-1.5 text-sm font-medium text-[#5652B0] bg-purple-50 rounded-md">
                    All Vouchers
                  </button>
                  <button className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-[#5652B0] hover:bg-purple-50 rounded-md">
                    Active
                  </button>
                  <button className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-[#5652B0] hover:bg-purple-50 rounded-md">
                    Expired
                  </button>
                </div>
                <button className="inline-flex items-center px-4 py-2 border-2 border-[#5652B0] rounded-lg text-sm font-medium text-[#5652B0] bg-white hover:bg-[#5652B0] hover:text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Voucher
                </button>
              </div>

              <div className="space-y-4">
                <ControlledAccordions />
                <ControlledAccordions />
              </div>
            </div>
          )}
        </div>

        <BlockAccountModal
          isOpen={isBlockModalOpen}
          onClose={() => setIsBlockModalOpen(false)}
          onConfirm={handleStatusUpdate}
          isBlocking={!vcConfigSettings.customerBlocked}
          businessName={businessName}
          isLoading={isUpdatingStatus}
        />
      </div>
    );
  }
};

export default CustomerOverviewTab;
